import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ResultCodeFacadeService } from '@app/facade-services/result-code/result-code-facade.service';
import { Subject } from 'rxjs';
import { DataItem } from '@app/components/shared/model/data-item';
import { KombinationstypEnum } from '@app/constants/kombinationstyp';
import { RiderClassFence } from '@app/components/fence-result-audience/model/RiderClassFence';
import { ResultCode } from '@app/rest-client/faxe/models/result-code';
import { mapRiderClassFence, mapTable } from '@app/components/fence-result-audience/mapper/mapper-service';
import { Table } from '@app/components/shared/table/model/table';
import { RiderDataItem } from '@app/components/fence-result-audience/model/rider-data-item';
import { SessionKeys } from '@app/constants/session-keys';
import { RaceClass } from '@app/rest-client/faxe/models/race-class';
import { RiderView } from '@app/facade-services/rider-view/model/rider-view';
import {
  MatAccordion, MatExpansionModule,
  MatExpansionPanel
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { CollapisbleSectionModule } from '@app/components/shared/collapisble-section/collapisble-section.module';
import { CommonModule, NgClass } from '@angular/common';
import { TableComponent } from '@app/components/shared/table/table.component';
import { MatCardModule } from '@angular/material/card';
import { distict, isNotEmpty, stringToNumber, toMap } from '@app/fremework/functions';
import { ChipListComponent } from '@app/components/shared/chip-list/chip-list.component';
import { getRiderLastFenceName, getRiderLastFenceSequence, raceDuration, sortWheight } from '@app/domain/functions';
import { PageLayoutComponent } from '@app/components/shared/page-layout/page-layout.component';
import { ClubbLoggoService } from '@app/services/clubb-loggo.service';
import { RaceViewService } from '@app/facade-services/race-view/race-view.service';
import { RaceView } from '@app/facade-services/race-view/model/race-view';
import { RaceClassView } from '@app/facade-services/race-view/model/race-class-view';

@Component({
  selector: 'faxe-fence-result-audience',
  templateUrl: './fence-result-audience.component.html',
  styleUrls: ['./fence-result-audience.component.scss'],
  imports: [
    CommonModule,
    MatAccordion,
    MatExpansionPanel,
    MatIcon,
    MatExpansionModule,
    MatProgressBar,
    MatCardModule,
    CollapisbleSectionModule,
    NgClass,
    TableComponent,
    ChipListComponent,
    PageLayoutComponent
  ]
})
export class FenceResultAudienceComponent implements OnInit, OnDestroy {
  riderDataItems: DataItem<RiderClassFence>[];
  riderViews: RiderView[];
  ridersFilteredToShow: RiderView[];
  raceView: RaceView;
  resultCodeMap: Map<string, ResultCode>;
  riderTimeMap: Map<string, string> = new Map<string, string>();
  resultCodes: ResultCode[];
  tableDataMap: Map<string, Table<RiderDataItem>> = new Map<string, Table<RiderDataItem>>();
  riderColapsMap: Map<string, boolean> = new Map<string, boolean>();
  viewMode: boolean;
  raceClassDataItem: DataItem<RaceClass>[];
  selectedClassGuids: Set<string> = new Set<string>();
  formGroup: UntypedFormGroup = this.fb.group({
    value: new UntypedFormControl(null)
  });
  allClassId = 'ghostRiders';
  allClassName = 'Aktiva klasser';
  selectedClassNameList: string;
  intervalId;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private readonly resultCodeFacadeService: ResultCodeFacadeService,
    private fb: UntypedFormBuilder,
    private readonly raceViewService: RaceViewService,
    private clubbLoggoService: ClubbLoggoService,
  ) {
  }

  ngOnInit(): void {
    this.setVeiwMode(sessionStorage.getItem(SessionKeys.viewMode));
    this.selectAllResultCodes();
    this.timer();
  }

  selectRaceView(): void {
    this.raceViewService
      .selectRaceView(true, true, false, 'FenceResultAudienceComponent')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(raceView => {
          this.raceView = raceView;
          this.raceClassDataItem = this.map(this.raceView);

          raceView.riders?.forEach(riderView => this.riderTimeMap?.set(riderView.riderGuid, raceDuration(riderView)));
          this.riderViews = raceView.riders;
          this.mapRiderDetails(raceView.riders);
          this.ridersToShow();
        }
      );
  }

  map(raceView: RaceView): DataItem<RaceClass>[] {
    const mapped: DataItem<RaceClass>[] = raceView
      .raceClassView
      ?.map(rc => ({
        displayname: `${rc.raceClassNumber}  (${rc.raceClassName})`,
        key: rc.raceClassGuid,
        value: rc,
        sortOrder: stringToNumber(rc.raceClassNumber),
        kombinationstypEnum: KombinationstypEnum.canNExcludeOthers
      }));

    mapped.push(({
      displayname: this.allClassName,
      key: this.allClassId,
      value: { raceClassNumber: '-1', raceClassGuid: this.allClassId, raceGuid: undefined },
      sortOrder: 0,
      kombinationstypEnum: KombinationstypEnum.can1AndExcludeOthers,
      default: this.allClassId
    }));

    return mapped
      ?.sort((a, b) => stringToNumber(a.value.raceClassNumber) - stringToNumber(b.value.raceClassNumber));
  }

  setVeiwMode(value: string) {
    this.viewMode = value === null ? false : value === 'true';
    if (this.viewMode) {
      this.selectedClassGuids.add(this.allClassId);
    }
  }

  private logoSrc(clubName: string | undefined): string {
    return this.clubbLoggoService.logoSrc(clubName);
  }

  mapRiderDetails(riderViews: RiderView[]) {
    riderViews.forEach(riderView => {
      this.tableDataMap.set(
        riderView.riderGuid,
        mapTable(riderView, this.raceView, this.resultCodes));
    });
  }

  filterRiders(riderViews: RiderView[]): RiderView[] {
    if (isNotEmpty(riderViews)) {
      if (this.selectedClassGuids.has(this.allClassId)) {
        return this.filterClassOnTrack(riderViews);
      }
      return riderViews
        .filter(rider => this.selectedClassGuids.has(rider.raceClassGuid));
    }
  }

  filterClassOnTrack(riderViews: RiderView[]): RiderView[] {
    const classesOnTrack = distict(riderViews
        .filter(rider => rider.isReportable)
        .map(rider => rider),
      'raceClassGuid')
      .map(r => r.raceClassGuid);

    return riderViews
      .filter(rider => classesOnTrack.find(classOnTrack => rider.raceClassGuid === classOnTrack));
  }

  selectAllResultCodes(): void {
    this.resultCodeFacadeService.fetchAllResultCodes('FenceResultAudienceComponent')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(resultCodes => {
          this.resultCodes = resultCodes;
          this.resultCodeMap = toMap(resultCodes, r => r.resultCodeGuid);
          this.selectRaceView();
        }
      );
  }

  mapRiders(riderView: RiderView, raceClassView: RaceClassView[], resultCodeMap: Map<string, ResultCode>): DataItem<RiderClassFence> {
    return {
      key: riderView.riderGuid,
      displayname: riderView.horseCombinationNumber.toString(),
      kombinationstypEnum: KombinationstypEnum.must1of1,
      value: mapRiderClassFence(riderView, raceClassView, resultCodeMap),
      sortOrder: getRiderLastFenceSequence(riderView),
      badge: getRiderLastFenceName(riderView),
      imageSrc: this.logoSrc(riderView.clubName),
    };
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    clearInterval(this.intervalId);
  }

  handleCollapse($event: boolean, riderId: string) {
    this.riderColapsMap.set(riderId, $event);
  }

  isCollapsed(riderId: string) {
    if (!this.riderColapsMap.has(riderId)) {
      return true;
    }
    return this.riderColapsMap.get(riderId);
  }

  classSelected($event: DataItem<RaceClass>) {
    this.selectedClassGuids.add($event.value.raceGuid);
    this.selectedClassNameList = this.joinSelectedRaceClasses(this.selectedClassGuids);
    this.ridersToShow();
  }

  classUnselected($event: DataItem<RaceClass>) {
    this.selectedClassGuids.delete($event.value.raceClassGuid);
    this.selectedClassNameList = this.joinSelectedRaceClasses(this.selectedClassGuids);
    this.ridersToShow();
  }

  joinSelectedRaceClasses(selectedClasses: Set<string>): string {

    if (selectedClasses.has(this.allClassId)) {
      return this.allClassName;
    }

    const selectedClassesO = this.raceView.raceClassView
      .filter(rc => selectedClasses.has(rc.raceClassGuid));

    const name = selectedClassesO
      .map(rc => rc.raceClassName)
      .join(', ');

    return name.length > 25 ? selectedClassesO[0].raceClassName + ' m.fl.' : name;
  }

  classPreSelected($event: DataItem<RaceClass>[]) {
    $event
      .map(rc => rc.value.raceClassGuid)
      .forEach(i => this.selectedClassGuids.add(i));

    this.selectedClassNameList = this.joinSelectedRaceClasses(
      new Set<string>($event.map(i => i.key)));
    this.ridersToShow();
  }

  timer() {
    this.intervalId = setInterval(() => {
      this.ridersFilteredToShow?.forEach(rider => {
        this.riderTimeMap?.set(rider.riderGuid, raceDuration(rider));
      });
    }, 500);
  }

  ridersToShow() {
    this.ridersFilteredToShow = this.filterRiders(this.riderViews);
    this.riderDataItems = this.ridersFilteredToShow
      .sort((n1, n2) =>
        sortWheight(n2) -
        sortWheight(n1))
      .map(rider => this.mapRiders(rider, this.raceView.raceClassView, this.resultCodeMap));
  }
}
